import {
  CandleFetchParams,
  CandlePoint,
  ExchangeCandleRestAdapter,
} from "../../candle.types";
import { toBinanceInterval } from "../../candle-intervals";
import { normalizeCandleSymbol } from "../../candle-symbols";
import {
  dedupeCandles,
  throwHttpError,
} from "./rest-adapter.utils";

type BinanceKline = [
  number,
  string,
  string,
  string,
  string,
  string,
  number,
  string,
  number,
  string,
  string,
  string
];

interface BinanceErrorBody {
  code?: number;
  msg?: string;
}

const BINANCE_KLINES_URL = "https://fapi.binance.com/fapi/v1/klines";
const BINANCE_MARK_PRICE_KLINES_URL =
  "https://fapi.binance.com/fapi/v1/markPriceKlines";
const DEFAULT_LIMIT = 500;
const MAX_LIMIT = 1500;
/** Binance: contract exists but is not TRADING yet (for example PENDING_TRADING). */
const INVALID_SYMBOL_STATUS = -1122;

function buildKlineQuery(symbol: string, params: CandleFetchParams): string {
  const query = new URLSearchParams({
    symbol,
    interval: toBinanceInterval(params.interval),
    limit: String(Math.min(params.limit ?? DEFAULT_LIMIT, MAX_LIMIT)),
  });

  if (params.startTime) query.set("startTime", String(params.startTime));
  if (params.endTime) query.set("endTime", String(params.endTime));

  return query.toString();
}

function mapKlines(payload: BinanceKline[]): CandlePoint[] {
  const candles: CandlePoint[] = payload.map((kline) => ({
    startTime: kline[0],
    openPrice: kline[1],
    highPrice: kline[2],
    lowPrice: kline[3],
    closePrice: kline[4],
    volume: kline[5],
  }));

  return dedupeCandles(candles);
}

async function readBinanceError(response: Response): Promise<BinanceErrorBody> {
  try {
    return (await response.json()) as BinanceErrorBody;
  } catch {
    return {};
  }
}

function isPreTradingSymbol(status: number, body: BinanceErrorBody): boolean {
  return (
    status === 400 &&
    (body.code === INVALID_SYMBOL_STATUS ||
      body.msg?.toLowerCase().includes("invalid symbol status") === true)
  );
}

async function fetchKlines(url: string): Promise<CandlePoint[]> {
  const response = await fetch(url);
  if (!response.ok) {
    throwHttpError(response, "binance");
  }

  const payload = (await response.json()) as BinanceKline[];
  return mapKlines(payload);
}

export const binanceCandlesRestAdapter: ExchangeCandleRestAdapter = {
  async fetchCandles(params: CandleFetchParams): Promise<CandlePoint[]> {
    const symbol = normalizeCandleSymbol("binance", params.symbol);
    const query = buildKlineQuery(symbol, params);
    const tradeResponse = await fetch(`${BINANCE_KLINES_URL}?${query}`);

    if (tradeResponse.ok) {
      const payload = (await tradeResponse.json()) as BinanceKline[];
      return mapKlines(payload);
    }

    const errorBody = await readBinanceError(tradeResponse);
    // Pre-open listings reject trade klines. Mark-price candles are already publishing.
    if (isPreTradingSymbol(tradeResponse.status, errorBody)) {
      return fetchKlines(`${BINANCE_MARK_PRICE_KLINES_URL}?${query}`);
    }

    throwHttpError(tradeResponse, "binance");
  },
};

